import React, { useMemo } from 'react';
import { AppLogo } from '@atlaskit/navigation-system';
import {
	createRouterSelector,
	getRouterState,
} from '@atlassian/react-resource-router/src/controllers/router-store';
import type { RouteGroup } from '@atlassian/jira-route-name-types/src/RouteGroup.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { ProductName } from '@atlassian/jira-shared-types/src/product-name.tsx';
import { projectsDirectoryRoute } from '@atlassian/jira-router-routes-directories-routes/src/projectsDirectoryRoute.tsx';
import { projectsDirectoryLegacyPathRedirectRoute } from '@atlassian/jira-router-routes-directories-routes/src/projectsDirectoryLegacyPathRedirectRoute.tsx';
import type { JiraProjectType } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { getProductIcon, getLogoLabel } from '../utils.tsx';

export const useCurrentRouteGroup = createRouterSelector((state) => state.route.group);

export function JSMAndCSMLogo({
	navigateToHome,
	testIdPrefix,
	homeURL,
	projectType,
}: {
	testIdPrefix?: string;
	navigateToHome: React.MouseEventHandler<HTMLAnchorElement>;
	homeURL: string;
	projectType: JiraProjectType | null | undefined;
}) {
	const { formatMessage } = useIntl();
	const routeGroup: RouteGroup | undefined = useCurrentRouteGroup();

	const { label, icon, name } = useMemo(() => {
		const selectedProductName = projectType
			? getProductNameByProjectType(projectType)
			: getProductNameByRouteGroup(routeGroup);

		return {
			label: getLogoLabel(formatMessage, selectedProductName),
			...getProductIcon(selectedProductName, testIdPrefix),
		};
	}, [formatMessage, projectType, routeGroup, testIdPrefix]);

	return <AppLogo label={label} icon={icon} name={name} href={homeURL} onClick={navigateToHome} />;
}

const PRODUCT_NAME_MAP: Record<string, ProductName> = {
	CUSTOMER_SERVICE: 'customer-service',
	SERVICE_DESK: 'serviceDesk',
	DEFAULT: 'jira',
};

function getProductNameByProjectType(projectType: JiraProjectType): ProductName {
	switch (projectType) {
		case 'CUSTOMER_SERVICE':
			return PRODUCT_NAME_MAP.CUSTOMER_SERVICE;
		case 'SERVICE_DESK':
			return PRODUCT_NAME_MAP.SERVICE_DESK;
		default:
			return PRODUCT_NAME_MAP.DEFAULT;
	}
}

function getProductNameByRouteGroup(routeGroup: RouteGroup | undefined): ProductName {
	switch (routeGroup) {
		case 'directories': {
			// There are two routes that can be used to navigate to the projects directory:
			// 1. projectsDirectoryRoute
			//    This is the route users land on when they directly navigate to the projects directory
			//    e.g. /jira/projects?page=1&sortKey=name&sortOrder=ASC&types=customer_service
			//    Note the query param is named "types" for the project types
			// 2. projectsDirectoryLegacyPathRedirectRoute
			//    This route is from the app switcher that uses a legacy path with a redirect
			//    e.g. /secure/BrowseProjects.jspa?selectedProjectType=customer_service
			//    Note the query param is named "selectedProjectType" for the project types
			// We need to check both routes and query params to determine the product name.
			const routerState = getRouterState();
			if (
				routerState.route.name === projectsDirectoryRoute.name ||
				routerState.route.name === projectsDirectoryLegacyPathRedirectRoute.name
			) {
				const projectTypes = routerState.query?.types || routerState.query?.selectedProjectType;
				const typesArray = projectTypes?.split(',')?.filter(Boolean) ?? [];

				if (typesArray.includes('customer_service')) {
					return PRODUCT_NAME_MAP.CUSTOMER_SERVICE;
				}

				if (typesArray.includes('service_desk')) {
					return PRODUCT_NAME_MAP.SERVICE_DESK;
				}
			}

			return PRODUCT_NAME_MAP.DEFAULT;
		}
		case 'customer-service':
			return PRODUCT_NAME_MAP.CUSTOMER_SERVICE;
		case 'servicedesk':
			return PRODUCT_NAME_MAP.SERVICE_DESK;
		default:
			return PRODUCT_NAME_MAP.DEFAULT;
	}
}
